import { useId } from 'react';
import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

import { TOP_UP_METHODS, type TopUpMethod } from '@/features/wallet/topUpMethod.js';

const METHOD_ICON: Record<TopUpMethod, string> = {
  fpx: 'account_balance',
  ewallet: 'account_balance_wallet',
};

/**
 * "How do you want to pay?" on the Top up card (MYP-135): online banking (Fiuu FPX) or e-wallet /
 * DuitNow QR (Revenue Monster), as two choice cards — the old site's 付款方式 dropdown, made easy
 * to tap on a phone.
 *
 * Real radio buttons inside labels, so arrow keys move between the two and a screen reader reads
 * one group. The input is visually hidden; the card shows the choice and the focus ring.
 */
export function TopUpMethodPicker({
  value,
  onChange,
  disabled,
}: {
  value: TopUpMethod;
  onChange: (method: TopUpMethod) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const name = useId();

  return (
    <fieldset disabled={disabled} className="@container min-w-0">
      <legend className="mb-2 block text-sm font-semibold text-ink">
        {t('wallet.topUp.methodLabel')}
      </legend>
      <div className="grid grid-cols-1 gap-2 @md:grid-cols-2">
        {TOP_UP_METHODS.map((method) => {
          const checked = value === method;
          return (
            <label
              key={method}
              className={cn(
                'flex min-h-16 cursor-pointer items-center gap-3 rounded-lg border bg-surface px-3.5 py-3 transition-colors',
                'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand',
                checked
                  ? 'border-brand bg-brand-soft ring-1 ring-brand'
                  : 'border-line-strong hover:bg-surface-alt',
              )}
            >
              <input
                type="radio"
                name={name}
                value={method}
                checked={checked}
                onChange={() => onChange(method)}
                className="sr-only"
              />
              <Icon name={METHOD_ICON[method]} size={26} className="shrink-0 text-ink" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-sm font-semibold text-ink">
                  {t(`wallet.topUp.methods.${method}.name`)}
                </span>
                <span className="text-xs text-muted">
                  {t(`wallet.topUp.methods.${method}.detail`)}
                </span>
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  'size-[18px] shrink-0 rounded-full border-2 bg-surface',
                  checked ? 'border-[6px] border-brand' : 'border-line-strong',
                )}
              />
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
